import _ from 'lodash';
import React, {Component, Fragment} from 'react';
import PropTypes from 'prop-types';
import {observer} from 'mobx-react';
import AsyncPaginate, { reduceGroupedOptions }  from "react-select-async-paginate";

import {getPublishingDestinations} from './socialApi'
import ImageCircle from "../../components/ImageCircle";
import {formatPostalAddress} from "../../services/locations/locationAddress/util";
import socialPublishingStore from "./store/SocialPublishingStore";
import socialPagesStore from "./publishing/SocialPagesStore";
import reactSelectStyles from '../../services/config/reactSelect/reactSelectStyles';
import reactSelectTheme from '../../services/config/reactSelect/reactSelectTheme';
import {IndicatorsContainer,DropdownIndicator,IndicatorSeparator} from '../../services/config/reactSelect/components';
import selectedLocationStore from "../../services/locations/SelectedLocationsStore";
import * as mobx from "mobx";

/**
 * Used for selecting where to post a social post to; can select from:
 * - facebook pages
 * - facebook publishing groups
 * - twitter accounts
 */
@observer
export class PublishingDestinationSelector extends Component {

  constructor(props) {
    super(props);
  }

  options = { options: [] };
  optionsCache;
  selectEverythingInProcess = false;


  static propTypes = {
    provider: PropTypes.string,
    modalMode: PropTypes.bool,
    excludeGroups: PropTypes.bool,
    noResultsText: PropTypes.string,
    width: PropTypes.string,
    selected: PropTypes.array,
    isDisabled: PropTypes.bool,
    excludeOptions: PropTypes.arrayOf(PropTypes.shape({ key: PropTypes.string })),
    onSelected: PropTypes.func.isRequired
  };

  static defaultProps = {
    width: '250px',
    excludeOptions: []
  };


  renderOption = (item) => {

    function renderListing(listing) {
      return (
        <div key={listing.id}>
          {listing.imageUrl && (listing.pageType === "User") // unfortunately, pages seem to be getting their credential images added
            ? <ImageCircle style={{verticalAlign: 'middle'}} size={25} src={listing.imageUrl}/>
            : <span className="listing=group-icon"><i className="ss-icon ss-location"/></span>
          }
          <span style={{verticalAlign: 'middle', marginLeft: '5px', lineHeight: '1rem', display: 'inline-block', maxWidth: 'calc(100% - 30px)'}}>
            {listing.label} <br/>
            <em style={{color: '#AAA'}}>{listing.address && formatPostalAddress(listing.address)}</em>
          </span>
        </div>
      );
    }

    function renderListingGroup(group) {
      return <div key={group.id}>
        <span className="listing-group-icon"><i className={"fas fa-map-marker-plus fa-2x"}/></span>
        <span style={{verticalAlign: 'middle', marginLeft: '5px', display: 'inline-block', maxWidth: 'calc(100% - 30px)'}}>
        {group.label} <span className="light-grey-text">[{group.remoteIds.length}]</span>
      </span>
      </div>
    }

    function renderAccount(account) {
      return <div key={account.id}>
        <ImageCircle style={{verticalAlign: 'middle'}} size={25} src={account.imageUrl} />
        <span style={{verticalAlign: 'middle', marginLeft: '5px', display: 'inline-block', maxWidth: 'calc(100% - 30px)'}}>
          {account.label}
        </span>
      </div>
    }

    const {pageType} = item;
    switch (pageType) {
      case "Place": return renderListing(item);
      case "Group":
        if (this.props.excludeGroups) return null;
        return renderListingGroup(item);
      case "User": return renderAccount(item);
      default:
        console.error("Invalid PageType received for item", item);
        return null;
    }
  };

  loadAllOptions = async () => {
    function addUniqueGroups(accumulator, op) {
      let accumulatorIds = accumulator.map(group => group.id)
      const groups  = op.options[0].options || []
      groups.forEach(group => {
        if (!accumulatorIds.includes(group.id)) {
          accumulator.push(group)
        }
      })
    }

    if(!this.optionsCache){
      let op;
      let accumulator = [[],[]];
      let curOffset = 0;
      do {
        let searchParameters = {offset: curOffset}
        op = await this.loadNextOptions(null, searchParameters)

        addUniqueGroups(accumulator[0], op); //paging produces duplicate groups so filter those out
        accumulator[1] = accumulator[1].concat(op.options[1].options)

        if(op.additional){curOffset=op.additional.offset}
      } while(op.hasMore)

      this.options.options[0].options=accumulator[0] //groups
      this.options.options[1].options=accumulator[1] //listings
      this.options.hasMore = false
      this.options.additional.offset = op.additional.offset
      this.optionsCache = _.cloneDeep(this.options)
    } else {
      //load from cache
      this.options = this.optionsCache
    }



  }

  loadNextOptions = async (searchText, searchParams) => {
    const limit = 50;
    function getOffset(searchParams){
      let myOffset = 0;
      if(searchParams && searchParams.offset) {
        myOffset =  searchParams.offset
      } else {
        myOffset = searchParams[1] && searchParams[1].options ? searchParams[1].options.length : 0;
      }
      return myOffset
    }

    const offset = getOffset(searchParams)

    // Certain users, e.g. resellers, have access to many sub-accounts. If they select an account with a different billing account, they should still be able
    // to publish to all the pages they have access to. In the Location dropdown, the user can select an entity (All, Account, Group, or Location) that they
    // have access to. If they do, use the primaryAccountId of the selected EntityId to determine their publishing destinations.
    const selectedEntity = selectedLocationStore.selectedEntity;
    const accountId = selectedEntity && selectedEntity.primaryAccountId ? selectedEntity.primaryAccountId : this.props.accountId;
    const results = await getPublishingDestinations(accountId, {
      provider: this.props.provider,
      text: searchText,
      offset: offset,
      limit: limit
    });

    let opts = [];

    if (!this.props.excludeGroups) {
      opts = opts.concat({
        label: "Publishing Groups",
        options: results.publishingGroups
          .filter(g =>
            g.remoteIds && g.remoteIds.length > 0 &&
            g.provider.toLowerCase() === this.props.provider.toLowerCase()
          )
          .map(g => ({...g, value: g.id, pageType: "Group"}))
      });
    }

    opts = opts.concat({
      label: "Pages",
      options: results.listingPages
        .map(p => ({...p, value: p.id}))
    })
    this.options = {options: opts, hasMore: results.hasMore, additional: {offset: offset + limit}}
    return this.options;
  }

  async selectEverything() {
    if(!this.selectEverythingInProcess){
      this.selectEverythingInProcess = true
      await this.loadAllOptions()
      let allOptions = this.options.options.flatMap(x => x.options);
      this.props.onSelected(allOptions);
      this.selectEverythingInProcess = false
    }
  }

  render() {
    // let selectedValues = this.props.selected ? this.props.selected : [];
    // re-hydrate here?
    // - if credential: pull from that publishingCredentialsStore
    // - if page: pull from that other one which I can't recall right now
    let selectedValues;
    this.optionsCache = undefined
    if (!this.props.selected) {
      selectedValues = [];
    } else {
      selectedValues = this.props.selected.map(p => {
        // TODO #socialapprovalse
        // ugh, sometimes has just id, sometimes just value, depending on how it was loaded
        // make models consistent rather than this insecurity
        const id = (p.id || p.value);
        if (id.startsWith("Group")) {
          return {...p, value: id, id} // group?
        } else {
          // strip the prefix, either "Place" or "User" or somesuch... not really that important. 
          // These prefixes are a little bit uncertain about what they mean
          const subId = id.split(":").slice(1).join(":"); 
          let page = socialPagesStore.page(this.props.provider, subId);
          if (page) {
            return {...page, value: id, id };
          }
        }
      });
    }

    let maybeModalModeProps = this.props.modalMode
      ? (
        {
          menuPortalTarget: document.body,
          menuShouldBlockScroll: true,
          menuPlacement: 'auto'
        }
      )
      : {};


    return (
      <div className="d-flex align-items-center">
        <div className="mr-2 react-select" style={{width: this.props.width}}>
          <AsyncPaginate
            key={this.props.provider}
            styles={reactSelectStyles()}
            theme={reactSelectTheme}
            components={{IndicatorsContainer,DropdownIndicator,IndicatorSeparator}}
            className="react-select publishing-dest-container"
            classNamePrefix="r-select"
            value={selectedValues}
            isMulti={this.props.multi}
            isLoading={socialPublishingStore.isLoading}
            onChange={opt => this.props.onSelected(opt)}
            formatOptionLabel={this.renderOption}
            getOptionValue={(option) => option.value}
            noResultsText={this.props.noResultsText}
            isDisabled={this.props.isDisabled}
            loadOptions={(a, b, c) => this.loadNextOptions(a, b, c)}
            reduceOptions={reduceGroupedOptions}
            {...maybeModalModeProps}
          />
        </div>
        {
          this.props.multi &&
          <Fragment>
            <a onClick={() => this.props.onSelected([])}>Clear</a> |
            <a onClick={() =>  this.selectEverything()}>Select All</a>
          </Fragment>
        }
      </div>
    );
  }
}
